// src/pages/WorldPage.js  – Three.js 3D interactive world
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';

const ZONES = [
  { id:'html',       label:'HTML ZONE',  desc:'Master the structure of the web', color:0xff6432, x:-18, z:0  },
  { id:'css',        label:'CSS ZONE',   desc:'Style and animate your creations', color:0x3296ff, x:0,  z:-18 },
  { id:'javascript', label:'JS ZONE',    desc:'Bring logic and interactivity',    color:0xffd700, x:18, z:0  },
];

export default function WorldPage() {
  const canvasRef   = useRef(null);
  const stateRef    = useRef({});          // mutable refs without re-render
  const [popup,     setPopup]   = useState(null);
  const [fps,       setFps]     = useState(0);
  const { sendWorldPosition }   = useSocket();
  const { user }                = useAuth();
  const navigate                = useNavigate();

  const closePopup = useCallback(() => setPopup(null), []);

  const enterZone = useCallback(() => {
    if (!popup) return;
    navigate(`/missions?track=${popup.id}`);
  }, [popup, navigate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ── Renderer ────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.setClearColor(0x050a0f);

    // ── Scene ────────────────────────────────────────────────
    const scene  = new THREE.Scene();
    scene.fog    = new THREE.Fog(0x050a0f, 35, 85);
    const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 200);
    camera.position.set(0, 9, 18);

    // ── Lights ───────────────────────────────────────────────
    scene.add(new THREE.AmbientLight(0x0a1a28, 2.5));
    const dirLight = new THREE.DirectionalLight(0x00f5ff, 0.9);
    dirLight.position.set(12, 22, 12);
    dirLight.castShadow = true;
    scene.add(dirLight);

    const addPointLight = (color, intensity, distance, pos) => {
      const l = new THREE.PointLight(color, intensity, distance);
      l.position.copy(pos);
      scene.add(l);
    };
    addPointLight(0xff00ff, 0.5, 32, new THREE.Vector3(-18, 8, 8));
    addPointLight(0x39ff14, 0.4, 28, new THREE.Vector3(18, 8, 8));
    addPointLight(0x3296ff, 0.4, 28, new THREE.Vector3(0, 8, -18));

    // ── Ground ───────────────────────────────────────────────
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(90, 90),
      new THREE.MeshLambertMaterial({ color: 0x050d1c })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    const grid = new THREE.GridHelper(90, 45, 0x00f5ff, 0x0a1e35);
    grid.material.opacity    = 0.18;
    grid.material.transparent = true;
    scene.add(grid);

    // ── Stars ────────────────────────────────────────────────
    const starGeo = new THREE.BufferGeometry();
    const starArr = new Float32Array(900 * 3);
    for (let i = 0; i < 900; i++) {
      starArr[i*3]   = (Math.random() - 0.5) * 220;
      starArr[i*3+1] = 25 + Math.random() * 70;
      starArr[i*3+2] = (Math.random() - 0.5) * 220;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starArr, 3));
    scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.22 })));

    // ── Zone towers ──────────────────────────────────────────
    const zoneGroups = [];
    ZONES.forEach(z => {
      const g = new THREE.Group();
      g.position.set(z.x, 0, z.z);
      g.userData = z;

      // platform
      const platform = new THREE.Mesh(
        new THREE.CylinderGeometry(5.2, 5.8, 0.6, 20),
        new THREE.MeshLambertMaterial({ color: z.color, emissive: new THREE.Color(z.color).multiplyScalar(0.14) })
      );
      platform.position.y = 0.3;
      g.add(platform);

      // tower
      const tower = new THREE.Mesh(
        new THREE.BoxGeometry(3.2, 9, 3.2),
        new THREE.MeshLambertMaterial({ color: z.color, emissive: new THREE.Color(z.color).multiplyScalar(0.12), transparent: true, opacity: 0.85 })
      );
      tower.position.y = 5;
      tower.castShadow = true;
      g.add(tower);

      // glow ring
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(5.6, 0.18, 8, 36),
        new THREE.MeshBasicMaterial({ color: z.color })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.7;
      g.add(ring);

      // spinning beacon
      const beacon = new THREE.Mesh(
        new THREE.OctahedronGeometry(1.1),
        new THREE.MeshBasicMaterial({ color: z.color, wireframe: true })
      );
      beacon.position.y = 11;
      g.add(beacon);
      g.userData.beacon = beacon;

      // zone point light
      const zl = new THREE.PointLight(z.color, 1.8, 14);
      zl.position.y = 9;
      g.add(zl);

      scene.add(g);
      zoneGroups.push(g);
    });

    // ── Decorative buildings ─────────────────────────────────
    [[-30,5],[30,5],[-30,-25],[30,-25],[0,5],[-12,12],[12,12]].forEach(([x, z]) => {
      const h = 4 + Math.random() * 10;
      const b = new THREE.Mesh(
        new THREE.BoxGeometry(3 + Math.random() * 3, h, 3 + Math.random() * 3),
        new THREE.MeshLambertMaterial({ color: 0x0a1525 })
      );
      b.position.set(x, h / 2, z);
      b.castShadow = true;
      scene.add(b);
    });

    // ── Player ───────────────────────────────────────────────
    const player = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(0.42, 0.42, 1.7, 10),
      new THREE.MeshLambertMaterial({ color: 0x00e5f0 })
    );
    body.position.y = 0.95;
    player.add(body);
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.48, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0x00c8d8 })
    );
    head.position.y = 2.1;
    player.add(head);
    const playerLight = new THREE.PointLight(0x00f5ff, 1.2, 5);
    playerLight.position.y = 2;
    player.add(playerLight);
    player.position.set(0, 0, 10);
    scene.add(player);

    // ── Raycaster for clicks ─────────────────────────────────
    const raycaster = new THREE.Raycaster();
    const handleClick = (e) => {
      const rect  = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      for (const hit of hits) {
        let obj = hit.object;
        while (obj) {
          if (obj.userData?.id) { setPopup(obj.userData); return; }
          obj = obj.parent;
        }
      }
    };
    canvas.addEventListener('click', handleClick);

    // ── Keys ─────────────────────────────────────────────────
    const keys = {};
    const onDown = e => { keys[e.key] = true; };
    const onUp   = e => { keys[e.key] = false; };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup',   onUp);

    // ── Resize ───────────────────────────────────────────────
    const resize = () => {
      const W = canvas.clientWidth, H = canvas.clientHeight;
      if (W === 0 || H === 0) return;
      renderer.setSize(W, H, false);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
    };
    // Use window resize instead of ResizeObserver for broader compatibility
    window.addEventListener('resize', resize);
    resize();

    // ── Animation loop ────────────────────────────────────────
    const clock  = new THREE.Clock();
    let   frameCount = 0, lastFpsTime = performance.now();
    let   rafId  = 0;
    let   active = true;

    const animate = () => {
      if (!active) return;
      rafId = requestAnimationFrame(animate);
      const dt = clock.getDelta();
      const t  = clock.getElapsedTime();

      // FPS
      frameCount++;
      const now = performance.now();
      if (now - lastFpsTime > 1000) {
        setFps(frameCount);
        frameCount = 0;
        lastFpsTime = now;
      }

      // Beacons
      zoneGroups.forEach((z, i) => {
        if (z.userData.beacon) z.userData.beacon.rotation.y = t * 1.8 + i * 2.1;
        z.rotation.y = Math.sin(t * 0.25 + i) * 0.04;
      });

      // Player movement
      const speed = 7 * dt;
      const fwd   = new THREE.Vector3(-Math.sin(player.rotation.y), 0, -Math.cos(player.rotation.y));
      let moved   = false;
      if (keys['w'] || keys['ArrowUp'])    { player.position.addScaledVector(fwd,  speed); moved = true; }
      if (keys['s'] || keys['ArrowDown'])  { player.position.addScaledVector(fwd, -speed); moved = true; }
      if (keys['a'] || keys['ArrowLeft'])  { player.rotation.y += dt * 2.2; }
      if (keys['d'] || keys['ArrowRight']) { player.rotation.y -= dt * 2.2; }
      player.position.x = Math.max(-40, Math.min(40, player.position.x));
      player.position.z = Math.max(-40, Math.min(40, player.position.z));
      player.position.y = moved ? Math.abs(Math.sin(t * 9)) * 0.14 : player.position.y * 0.85;

      // Broadcast (throttled)
      if (moved && Math.floor(t * 5) !== Math.floor((t - dt) * 5)) {
        sendWorldPosition({ x: player.position.x, y: player.position.y, z: player.position.z, rotation: player.rotation.y });
      }

      // Camera follow
      const camTarget = player.position.clone().add(
        new THREE.Vector3(Math.sin(player.rotation.y) * 12, 9, Math.cos(player.rotation.y) * 12)
      );
      camera.position.lerp(camTarget, 0.07);
      camera.lookAt(player.position.clone().add(new THREE.Vector3(0, 1.5, 0)));

      // Proximity popup (auto-detect when within 5.5 units)
      let nearZone = null, nearDist = 5.5;
      zoneGroups.forEach(z => {
        const d = player.position.distanceTo(z.position);
        if (d < nearDist) { nearZone = z; nearDist = d; }
      });
      if (nearZone) {
        setPopup(p => p?.id === nearZone.userData.id ? p : nearZone.userData);
      } else {
        setPopup(p => (p !== null && nearDist > 6.5) ? null : p);
      }

      renderer.render(scene, camera);
    };
    animate();

    // Save cleanup refs
    stateRef.current = { renderer, rafId: () => rafId, active: () => active };

    return () => {
      active = false;
      cancelAnimationFrame(rafId);
      canvas.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup',   onUp);
      window.removeEventListener('resize',  resize);
      renderer.dispose();
    };
  }, []); // eslint-disable-line

  return (
    <div style={{ position:'relative', height:'100%', background:'#000', overflow:'hidden' }}>
      <canvas ref={canvasRef} style={{ width:'100%', height:'100%', display:'block', outline:'none' }}
        tabIndex={0} />

      {/* Controls HUD */}
      <div style={{ position:'absolute', top:'18px', left:'18px', zIndex:10,
                    background:'rgba(0,0,0,0.7)', border:'1px solid rgba(0,245,255,0.18)',
                    borderRadius:'9px', padding:'12px 16px', fontSize:'12px',
                    color:'rgba(255,255,255,0.5)', lineHeight:1.9 }}>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'10px', color:'#00f5ff', letterSpacing:'2px', marginBottom:'5px' }}>CONTROLS</div>
        <div>W / ↑ &nbsp;— Move forward</div>
        <div>S / ↓ &nbsp;— Move back</div>
        <div>A / ← &nbsp;— Turn left</div>
        <div>D / → &nbsp;— Turn right</div>
        <div>Click zone — Open mission</div>
      </div>

      {/* Minimap */}
      <div style={{ position:'absolute', top:'18px', right:'18px', width:'128px', height:'128px', zIndex:10,
                    background:'rgba(0,0,0,0.7)', border:'1px solid rgba(0,245,255,0.18)',
                    borderRadius:'9px', padding:'8px', display:'flex', flexDirection:'column', alignItems:'center' }}>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'9px', color:'#00f5ff', letterSpacing:'2px', marginBottom:'5px' }}>MAP</div>
        <svg width="100" height="90" viewBox="-45 -45 90 90">
          {ZONES.map(z => (
            <circle key={z.id} cx={z.x * 1.1} cy={z.z * 1.1} r="7"
              fill={`#${z.color.toString(16).padStart(6,'0')}`} opacity="0.8" />
          ))}
          <circle cx="0" cy="0" r="4" fill="#00f5ff" />
        </svg>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'9px', color:'rgba(255,255,255,0.25)', marginTop:'2px' }}>{fps} FPS</div>
      </div>

      {/* Player info */}
      <div style={{ position:'absolute', bottom:'58px', left:'18px', zIndex:10,
                    background:'rgba(0,0,0,0.7)', border:'1px solid rgba(0,245,255,0.18)',
                    borderRadius:'9px', padding:'9px 14px', display:'flex', alignItems:'center', gap:'9px' }}>
        <span style={{ fontSize:'20px' }}>{user?.avatar || '🧑‍💻'}</span>
        <div>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#fff' }}>{user?.username}</div>
          <div style={{ fontSize:'10px', color:'#00f5ff', letterSpacing:'1px' }}>LVL {user?.level} · {user?.xp} XP</div>
        </div>
      </div>

      {/* Zone popup */}
      {popup && (
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:20,
                      background:'rgba(13,17,23,0.97)', border:'1px solid rgba(0,245,255,0.3)',
                      borderRadius:'12px', padding:'22px', minWidth:'270px', textAlign:'center',
                      boxShadow:'0 0 50px rgba(0,245,255,0.12)' }}>
          <div style={{ fontFamily:'var(--font-game)', fontSize:'14px', fontWeight:700, color:'#00f5ff', marginBottom:'7px' }}>
            {popup.label}
          </div>
          <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.48)', marginBottom:'18px', lineHeight:1.5 }}>
            {popup.desc}
          </div>
          <button onClick={enterZone} style={{
            width:'100%', padding:'10px', marginBottom:'8px',
            background:'rgba(0,245,255,0.1)', border:'1px solid rgba(0,245,255,0.3)',
            borderRadius:'6px', color:'#00f5ff', cursor:'pointer',
            fontFamily:'var(--font-game)', fontSize:'11px', fontWeight:700, letterSpacing:'1.5px',
          }}>ENTER ZONE →</button>
          <button onClick={closePopup} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.28)', cursor:'pointer', fontSize:'12px' }}>
            ✕ Dismiss
          </button>
        </div>
      )}

      {/* Back button */}
      <button onClick={() => navigate('/')} style={{
        position:'absolute', bottom:'18px', left:'50%', transform:'translateX(-50%)', zIndex:10,
        padding:'9px 26px', background:'rgba(0,245,255,0.1)', border:'1px solid rgba(0,245,255,0.25)',
        borderRadius:'6px', color:'#00f5ff', cursor:'pointer',
        fontFamily:'var(--font-game)', fontSize:'10px', fontWeight:700, letterSpacing:'1.5px',
      }}>← BACK TO DASHBOARD</button>
    </div>
  );
}
